import React, { useEffect, useState, useMemo } from "react";
import { getProducts, type ProductDto } from "../api/products";
import { getCategories, type ProductCategoryDto } from "../api/productCategories";

import ProductDetailModal from "../components/products/ProductDetailModal";
import ProductTopBar from "../components/products/ProductTopBar";

import "../styles/pages/product-page.css";

export default function ProductPage() {
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [categories, setCategories] = useState<ProductCategoryDto[]>([]);

  const [search, setSearch] = useState("");

  const [filterType, setFilterType] = useState<"all" | "product" | "service">("all");
  const [filterStock, setFilterStock] = useState<"all" | "in" | "out">("all");
  const [filterCategory, setFilterCategory] = useState<number | "all">("all");

  const [sortStock, setSortStock] = useState<"none" | "asc" | "desc">("none");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<ProductDto | null>(null);

  async function loadProducts() {
    try {
      const res = await getProducts();
      setProducts(res ?? []);
    } catch (err) {
      console.error("loadProducts:", err);
    }
  }

  async function loadCategories() {
    try {
      const res = await getCategories();
      setCategories(res ?? []);
    } catch (err) {
      console.error("loadCategories:", err);
    }
  }

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const openDetail = (p: ProductDto) => {
    setDetailItem(p);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailItem(null);
  };

  const resetFilters = () => {
    setFilterType("all");
    setFilterStock("all");
    setFilterCategory("all");
    setSortStock("none");
    setSearch("");
  };

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();

    let result = products.filter((p) => {
      const name = (p.nameRu ?? "").toLowerCase();
      const sku = (p.sku ?? "").toLowerCase();
      const stock = typeof p.stock === "number" ? p.stock : 0;

      const bc = (p.barcode ?? "").toLowerCase();
      const matchesSearch = !q || name.includes(q) || sku.includes(q) || bc.includes(q);

      const matchesType =
        filterType === "all"
          ? true
          : filterType === "product"
          ? p.type === "product"
          : p.type === "service";

      const matchesStock =
        filterStock === "all"
          ? true
          : filterStock === "in"
          ? (p.type === "service" || p.type === "software" || stock > 0)
          : (p.type === "product" && stock === 0);

      const matchesCategory =
        filterCategory === "all"
          ? true
          : p.categoryId === filterCategory;

      return matchesSearch && matchesType && matchesStock && matchesCategory;
    });

    // 🔥 сортировка по остатку
    if (sortStock === "asc") {
      result = [...result].sort((a, b) => (Number(a.stock) || 0) - (Number(b.stock) || 0));
    }

    if (sortStock === "desc") {
      result = [...result].sort((a, b) => (Number(b.stock) || 0) - (Number(a.stock) || 0));
    }

    return result;
  }, [products, search, filterType, filterStock, filterCategory, sortStock]);

  return (
    <div className="product-page">

      <ProductTopBar
        search={search}
        onSearchChange={setSearch}

        filterType={filterType}
        onFilterTypeChange={setFilterType}

        filterStock={filterStock}
        onFilterStockChange={setFilterStock}

        filterCategory={filterCategory}
        onFilterCategoryChange={setFilterCategory}

        sortStock={sortStock}
        onSortStockChange={setSortStock}

        categories={categories}

        onResetFilters={resetFilters}
      />

      <main className="product-content">
        <div className="pc-products-grid apple-products-grid">
          {filteredProducts.length === 0 ? (
            <div className="muted">Товары не найдены</div>
          ) : (
            filteredProducts.map((p) => {
              const src = p.imageUrl
                ? p.imageUrl.startsWith("http")
                  ? p.imageUrl
                  : `${p.imageUrl}`
                : null;

              return (
                <article
                  key={p.id}
                  className="pc-product-card apple-product-card"
                  onClick={() => openDetail(p)}
                  role="button"
                >
                  <div className="pc-product-media">
                    {src ? (
                      <img src={src} alt={p.nameRu} />
                    ) : (
                      <div className="pc-product-noimage">Нет фото</div>
                    )}
                  </div>

                  <div className="pc-product-body">
                    <div className="pc-product-name">{p.nameRu}</div>

                    <div className="pc-product-meta">
                      <span className="pc-product-sku">
                        SKU: {p.sku ?? "—"}{p.barcode ? ` · ШК: ${p.barcode}` : ""}
                      </span>

                      <div className="pc-product-stock">
                        {p.type === "service" && (
                          <div className="product-tag-service">Услуга</div>
                        )}
                        {p.type === "software" && (
                          <div className="product-tag-service">ПО</div>
                        )}

                        {p.type === "product" && (Number(p.stock) || 0) === 0 && (
                          <div className="product-tag-out">Нет в наличии</div>
                        )}

                        {p.type === "product" && (Number(p.stock) || 0) > 0 && (
                         <div className="settings-tile-sub muted">Остаток: {p.stock} шт</div>
  )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </main>

      <ProductDetailModal
        open={detailOpen}
        item={detailItem ?? undefined}
        onClose={closeDetail}
      />
    </div>
  );
}
